import { useState } from "react";
import { AlertTriangle, Info, CheckCircle2 } from "lucide-react";
import type { UserSignal } from "@produce/shared";

/**
 * The "why is this user having a bad time" list.
 *
 * The rules live in @produce/shared (`buildUserSignals`) so they are unit
 * tested; this only decides how a signal looks. Each signal carries the rows
 * behind its count — a bare "2 invitations" sends support straight back to the
 * database, which is the thing this page exists to avoid.
 */

/** Detail lines shown before the list collapses behind a button. */
const PREVIEW_LINES = 6;

/**
 * Long detail lists are collapsed, never truncated.
 *
 * An earlier version cut the list off in the data with "…and 3 more", which
 * left the remaining rows unreachable from the page — the one thing an admin
 * cannot work around. How much fits on screen is a rendering decision, so it
 * lives here, and everything stays one click away.
 */
function SignalDetails({ signalId, details }: { signalId: string; details: string[] }) {
  const [expanded, setExpanded] = useState(false);
  const hidden = details.length - PREVIEW_LINES;
  const visible = expanded ? details : details.slice(0, PREVIEW_LINES);

  return (
    <>
      <ul className="mt-2 space-y-1">
        {visible.map((detail, index) => (
          <li
            key={`${signalId}-${index}`}
            className="break-words text-xs text-muted-foreground before:mr-2 before:content-['•']"
          >
            {detail}
          </li>
        ))}
      </ul>
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((open) => !open)}
          className="mt-2 text-xs font-medium underline underline-offset-2 hover:no-underline"
        >
          {expanded ? "Show less" : `Show all ${details.length}`}
        </button>
      )}
    </>
  );
}

export default function SignalList({ signals }: { signals: UserSignal[] }) {
  if (signals.length === 0) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
        No problems detected for this account.
      </div>
    );
  }

  return (
    <ul className="space-y-2">
      {signals.map((signal) => {
        const isWarning = signal.level === "warning";
        const Icon = isWarning ? AlertTriangle : Info;
        return (
          <li
            key={signal.id}
            className={`flex items-start gap-2 rounded-md border p-3 text-sm ${
              isWarning ? "border-destructive/30 bg-destructive/10" : "border-border bg-muted/40"
            }`}
          >
            <Icon
              className={`mt-0.5 h-4 w-4 shrink-0 ${
                isWarning ? "text-destructive" : "text-muted-foreground"
              }`}
            />
            <div className="min-w-0">
              <p>{signal.message}</p>
              {signal.details && signal.details.length > 0 && (
                <SignalDetails signalId={signal.id} details={signal.details} />
              )}
              {signal.hint && (
                <p className="mt-2 text-xs italic text-muted-foreground">{signal.hint}</p>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
